// ============================================
// MODALCARTABANCARIA.JS — Generar la carta de datos bancarios (v2, 2026-09-09)
// ============================================
// Se abre desde Datos de la empresa (carta libre) y desde la cotizacion (carta ligada).
//   - Se ELIGEN las cuentas que van impresas (todas marcadas por default, minimo una).
//   - La VIGENCIA se muestra siempre: desde una cotizacion hereda la de esa cotizacion (solo lectura);
//     libre usa la vigencia por defecto de Datos de la empresa.
//   - Al generar se descarga el PDF y se muestra el CODIGO de verificacion para copiarlo al correo
//     (no se imprime en el documento).
// Responsive: ancho min(94vw,520px), maxHeight 88vh con scroll, botones >= 44 px, textos >= 16 px.

import React, { useCallback, useEffect, useState } from 'react';
import { Landmark, Download, X, Copy, Check, AlertTriangle, CalendarClock } from 'lucide-react';
import { API_BASE_CLI, PP, COLOR_OSCURO, COLOR_GRIS, COLOR_GRIS_CLARO, COLOR_BORDE, estiloBotonPrimario, estiloBotonSecundario } from './helpersClientes';
import { authHeaders as authBase } from '../utils/storage';

const authJson = () => ({ ...authBase(), 'Content-Type': 'application/json' });
const fmtFecha = (v) => { if (!v) return '—'; const d = new Date(v); return isNaN(d) ? '—' : d.toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'America/Mexico_City' }); };

export default function ModalCartaBancaria({ abierto, onCerrar, cotizacionId = null, folioCotizacion = null, destino = 'descarga', onEmitida }) {
  const [opciones, setOpciones] = useState(null);
  const [seleccion, setSeleccion] = useState([]);
  const [anexos, setAnexos] = useState([]); // ids de cuenta cuya caratula SI se adjunta
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [copiado, setCopiado] = useState(false);

  const cargar = useCallback(async () => {
    setError(null); setOpciones(null); setResultado(null);
    try {
      const q = cotizacionId ? `?cotizacion_id=${encodeURIComponent(cotizacionId)}` : '';
      const r = await fetch(`${API_BASE_CLI}/api/empresa/documentos/carta-bancaria/opciones${q}`, { headers: authJson() });
      const j = await r.json().catch(() => ({}));
      if (r.status === 401 || r.status === 403) throw new Error('No tienes permiso para generar la carta de datos bancarios.');
      if (!r.ok || !j.ok) throw new Error(j.error || 'No se pudieron leer las cuentas.');
      setOpciones(j);
      setSeleccion((j.cuentas || []).map(c => c.id)); // todas las cuentas marcadas por default
      // El ANEXO se marca por default solo si la caratula esta VIGENTE. Una vencida puede ser un archivo
      // viejo con datos personales (la pagina 1 no lleva ningun nombre): se adjunta solo a proposito.
      setAnexos((j.cuentas || []).filter(c => c.tiene_caratula && c.caratula_estado !== 'vencido').map(c => c.id));
    } catch (e) { setError(e.message); }
  }, [cotizacionId]);

  useEffect(() => { if (abierto) cargar(); }, [abierto, cargar]);
  useEffect(() => {
    if (!abierto) return;
    const esc = (e) => { if (e.key === 'Escape') onCerrar(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [abierto, onCerrar]);

  if (!abierto) return null;

  const alternar = (id) => setSeleccion(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]));
  const alternarAnexo = (id) => setAnexos(a => (a.includes(id) ? a.filter(x => x !== id) : [...a, id]));
  const fmtCorta = (v) => { if (!v) return ''; const d = new Date(v); return isNaN(d) ? '' : d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'America/Mexico_City' }); };

  const generar = async () => {
    if (!seleccion.length) { setError('Marca al menos una cuenta bancaria.'); return; }
    setGenerando(true); setError(null); setCopiado(false);
    try {
      const url = cotizacionId
        ? `${API_BASE_CLI}/api/empresa/documentos/carta-bancaria/cotizacion/${encodeURIComponent(cotizacionId)}`
        : `${API_BASE_CLI}/api/empresa/documentos/carta-bancaria`;
      // `anexos` se manda SIEMPRE (aunque vaya vacio) para que el servidor respete lo marcado aqui
      // y no vuelva a su default; solo cuentan las cuentas que de verdad van en la carta.
      const r = await fetch(url, { method: 'POST', headers: authJson(), body: JSON.stringify({ cuentas: seleccion, anexos: anexos.filter(id => seleccion.includes(id)), destino }) });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || 'No se pudo generar la carta.'); }
      const blob = await r.blob();
      const folio = r.headers.get('X-Doc-Folio') || '';
      const a = document.createElement('a'); const href = URL.createObjectURL(blob);
      a.href = href; a.download = `Carta_datos_bancarios_Beep_${folio}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 30000);
      const aviso = r.headers.get('X-Doc-Aviso');
      setResultado({
        folio, codigo: r.headers.get('X-Doc-Codigo') || '',
        anexos: Number(r.headers.get('X-Doc-Anexo') || 0),
        expira: r.headers.get('X-Doc-Expira'), vigencia: r.headers.get('X-Doc-Vigencia'),
        aviso: aviso ? decodeURIComponent(aviso) : null,
      });
      if (onEmitida) onEmitida();
    } catch (e) { setError(e.message); }
    finally { setGenerando(false); }
  };

  const copiarCodigo = async () => {
    if (!resultado) return;
    const txt = `Folio: ${resultado.folio}\nCódigo de verificación: ${resultado.codigo}`;
    try { await navigator.clipboard.writeText(txt); setCopiado(true); setTimeout(() => setCopiado(false), 2500); }
    catch { setError('No se pudo copiar. Selecciona el texto y cópialo a mano.'); }
  };

  const fondo = { position: 'fixed', inset: 0, background: 'rgba(15,26,40,0.5)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 };
  const caja = { width: 'min(94vw, 520px)', maxHeight: '88vh', overflowY: 'auto', background: '#fff', borderRadius: 16, fontFamily: PP, color: COLOR_GRIS, boxShadow: '0 18px 44px rgba(0,0,0,0.24)' };
  const fila = (activa) => ({ display: 'flex', gap: 10, alignItems: 'flex-start', border: `1px solid ${activa ? '#265F97' : COLOR_BORDE}`, background: activa ? '#F3F8FD' : '#fff', borderRadius: 12, padding: 12, cursor: 'pointer', minHeight: 44 });

  return (
    <div style={fondo} onClick={onCerrar} role="dialog" aria-modal="true" aria-label="Generar carta de datos bancarios">
      <div style={caja} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 18px', borderBottom: `1px solid ${COLOR_BORDE}` }}>
          <Landmark size={20} color="#265F97" />
          <div style={{ fontSize: 15.5, fontWeight: 700, color: COLOR_OSCURO, flex: 1 }}>Generar carta de datos bancarios</div>
          <button type="button" aria-label="Cerrar" onClick={onCerrar} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: COLOR_GRIS_CLARO, minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={20} /></button>
        </div>

        <div style={{ padding: 18, display: 'grid', gap: 14 }}>
          {error && <div style={{ background: '#FDF2F2', border: '1px solid #F3C6C6', color: '#B42318', borderRadius: 10, padding: 12, fontSize: 13.5, display: 'flex', gap: 8 }}><AlertTriangle size={17} style={{ flex: 'none', marginTop: 1 }} /> <span>{error}</span></div>}

          {!opciones && !error && <div style={{ fontSize: 13.5, color: COLOR_GRIS_CLARO }}>Cargando cuentas…</div>}

          {opciones && !resultado && (
            <>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 8 }}>¿Qué cuentas van en la carta?</div>
                <div style={{ display: 'grid', gap: 8 }}>
                  {(opciones.cuentas || []).map(c => {
                    const activa = seleccion.includes(c.id);
                    return (
                      <label key={c.id} style={fila(activa)}>
                        <input type="checkbox" style={{ width: 20, height: 20, marginTop: 2, flex: 'none' }} checked={activa} onChange={() => alternar(c.id)} />
                        <span style={{ flex: 1 }}>
                          <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <strong style={{ color: COLOR_OSCURO, fontSize: 14 }}>{c.banco}</strong>
                            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: '#DEEBF7', color: '#265F97', fontWeight: 700 }}>{c.moneda}</span>
                            {c.principal && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: '#265F97', color: '#fff', fontWeight: 700 }}>Principal</span>}
                          </span>
                          <span style={{ display: 'block', fontSize: 12.5, color: COLOR_GRIS_CLARO, marginTop: 3 }}>
                            {c.numero_cuenta ? `Cuenta ${c.numero_cuenta}` : 'Sin número de cuenta'}{c.clabe_ultimos4 ? ` · CLABE ····${c.clabe_ultimos4}` : ''}
                            {!c.tiene_caratula && ' · sin carátula (no lleva anexo)'}
                          </span>
                          {/* Anexo por cuenta: la caratula VENCIDA llega desmarcada (puede ser un archivo
                              viejo con datos personales). Solo aparece si la cuenta va en la carta. */}
                          {c.tiene_caratula && activa && (
                            <span style={{ display: 'block', marginTop: 8, paddingTop: 8, borderTop: `1px dashed ${COLOR_BORDE}` }}>
                              <span
                                role="checkbox" aria-checked={anexos.includes(c.id)} tabIndex={0}
                                onClick={e => { e.preventDefault(); e.stopPropagation(); alternarAnexo(c.id); }}
                                onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); alternarAnexo(c.id); } }}
                                style={{ display: 'flex', gap: 8, alignItems: 'flex-start', minHeight: 44, cursor: 'pointer' }}
                              >
                                <input type="checkbox" readOnly tabIndex={-1} style={{ width: 18, height: 18, marginTop: 3, flex: 'none', pointerEvents: 'none' }} checked={anexos.includes(c.id)} />
                                <span style={{ fontSize: 12.5, color: c.caratula_estado === 'vencido' ? '#9A5B00' : COLOR_GRIS, lineHeight: 1.5 }}>
                                  {c.caratula_estado === 'vencido'
                                    ? <>Carátula vencida (emitida {fmtCorta(c.caratula_fecha_emision)}) — no se adjunta</>
                                    : <>Adjuntar la carátula del banco como anexo</>}
                                </span>
                              </span>
                            </span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>
                {!seleccion.length && <div style={{ fontSize: 12.5, color: '#B42318', marginTop: 6 }}>Marca al menos una cuenta.</div>}
              </div>

              <div style={{ background: '#F3F8FD', border: `1px solid ${COLOR_BORDE}`, borderRadius: 12, padding: 12, fontSize: 13, display: 'flex', gap: 9 }}>
                <CalendarClock size={17} color="#265F97" style={{ flex: 'none', marginTop: 1 }} />
                <div>
                  <div style={{ fontWeight: 700, color: COLOR_OSCURO }}>Vigencia: {opciones.vigencia_dias} días</div>
                  <div style={{ color: COLOR_GRIS_CLARO, marginTop: 2, lineHeight: 1.5 }}>
                    {opciones.vigencia_origen === 'cotizacion'
                      ? <>La toma de la cotización {opciones.folio_cotizacion || folioCotizacion || ''} y no se puede cambiar aquí. Si generas otra carta de esta cotización, la anterior queda revocada.</>
                      : <>Vigencia por defecto configurada en Datos de la empresa. Puedes cambiarla ahí.</>}
                  </div>
                </div>
              </div>

              <div style={{ fontSize: 12.5, color: COLOR_GRIS_CLARO, lineHeight: 1.55 }}>
                La carta se descarga con folio y fecha de vigencia impresos. El <strong>código de verificación NO se imprime</strong>: te lo damos al terminar para que lo pegues en el correo al cliente.
              </div>
            </>
          )}

          {resultado && (
            <div style={{ display: 'grid', gap: 12 }}>
              <div style={{ background: '#EAF7F0', border: '1px solid #B7E3CB', borderRadius: 12, padding: 14 }}>
                <div style={{ fontWeight: 700, color: '#1E7A4C', fontSize: 14.5 }}>Carta {resultado.folio} generada y descargada</div>
                <div style={{ fontSize: 13, color: COLOR_GRIS, marginTop: 5, lineHeight: 1.6 }}>
                  Vigente hasta el {fmtFecha(resultado.expira)} ({resultado.vigencia} días).<br />
                  {resultado.anexos > 0 ? `Incluye ${resultado.anexos} carátula${resultado.anexos > 1 ? 's' : ''} bancaria${resultado.anexos > 1 ? 's' : ''} como anexo.` : 'Sin anexos (ninguna cuenta tiene carátula subida).'}
                </div>
              </div>
              <div style={{ border: `1px solid ${COLOR_BORDE}`, borderRadius: 12, padding: 14 }}>
                <div style={{ fontSize: 12.5, color: COLOR_GRIS_CLARO, marginBottom: 6 }}>Código de verificación (mándalo por correo, no está en el PDF):</div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 20, fontWeight: 700, letterSpacing: 2, color: COLOR_OSCURO }}>{resultado.codigo}</span>
                  <button type="button" onClick={copiarCodigo} style={{ ...estiloBotonSecundario, minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {copiado ? <><Check size={15} color="#1E7A4C" /> Copiado</> : <><Copy size={15} /> Copiar folio y código</>}
                  </button>
                </div>
                <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 8, lineHeight: 1.5 }}>
                  Tu cliente lo comprueba en <strong>beepmx.com/verificar</strong> escribiendo el folio y este código.
                </div>
              </div>
              {resultado.aviso && <div style={{ background: '#FFF6E5', border: '1px solid #F3D9A4', color: '#9A5B00', borderRadius: 10, padding: 12, fontSize: 13 }}>{resultado.aviso}</div>}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', padding: '14px 18px', borderTop: `1px solid ${COLOR_BORDE}`, position: 'sticky', bottom: 0, background: '#fff' }}>
          {!resultado && (
            <button type="button" onClick={generar} disabled={generando || !opciones || !seleccion.length}
              style={{ ...estiloBotonPrimario, flex: '1 1 200px', minHeight: 46, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: (generando || !opciones || !seleccion.length) ? 0.6 : 1, cursor: (generando || !opciones || !seleccion.length) ? 'not-allowed' : 'pointer' }}>
              <Download size={16} /> {generando ? 'Generando…' : 'Generar y descargar'}
            </button>
          )}
          <button type="button" onClick={onCerrar} style={{ ...estiloBotonSecundario, flex: resultado ? '1 1 200px' : '0 1 120px', minHeight: 46 }}>{resultado ? 'Listo' : 'Cancelar'}</button>
        </div>
      </div>
    </div>
  );
}
